import React, { useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { useHybridInterestRate } from '@/hooks/useHybridInterestRate';

interface HybridInterestRateDisplayProps {
  loanProgram: USER_LOAN_PROGRAM;
  creditScore?: number;
  ltv?: number;
  /** Bridge: term in months (6/12/18/24) for admin tier pricing */
  bridgeTermMonths?: number;
  showAdjustments?: boolean;
}

export const HybridInterestRateDisplay: React.FC<
  HybridInterestRateDisplayProps
> = ({
  loanProgram,
  creditScore = 700,
  ltv = 80,
  bridgeTermMonths,
  showAdjustments = true,
}) => {
  const {
    backendRates,
    localAdjustments,
    creditScoreAdjustments,
    ltvAdjustments,
    needsRefresh,
    isStale,
    loading,
    error,
    setAdjustment,
    resetAdjustments,
    refreshRates,
    calculateAdjustedRate,
  } = useHybridInterestRate();

  const [showAdvanced, setShowAdvanced] = useState(false);

  const rateKey = loanProgram.toLowerCase() as keyof typeof backendRates;
  const rawBase = backendRates[rateKey];
  const baseRate =
    typeof rawBase === 'number' && Number.isFinite(rawBase) ? rawBase : null;
  const adjustments = localAdjustments[loanProgram];
  const effectiveRate = calculateAdjustedRate(
    loanProgram,
    creditScore,
    ltv,
    loanProgram === USER_LOAN_PROGRAM.BRIDGE
      ? { bridgeTermMonths: bridgeTermMonths ?? 12 }
      : undefined
  );

  // Handle local adjustments
  const handleBaseAdjustment = (value: number) => {
    setAdjustment(loanProgram, { baseAdjustment: value });
  };

  const handleCreditScoreAdjustment = (value: number) => {
    setAdjustment(loanProgram, { creditScoreAdjustment: value });
  };

  const handleLTVAdjustment = (value: number) => {
    setAdjustment(loanProgram, { ltvAdjustment: value });
  };

  const handleCustomRate = (value: number) => {
    setAdjustment(loanProgram, { customRate: value });
  };

  const handleReset = () => {
    resetAdjustments(loanProgram);
  };

  // Format rate for display
  const formatRate = (rate: number | null) =>
    rate === null || !Number.isFinite(rate) ? '—' : `${rate.toFixed(3)}%`;

  // Get cache status
  const getCacheStatus = () => {
    if (isStale) return 'Stale - needs refresh';
    if (needsRefresh) return 'Expired - needs refresh';
    return 'Fresh - cached';
  };

  return (
    <Card sx={{ p: 3, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" component="h3">
          Interest Rate: {loanProgram}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={getCacheStatus()}
            color={isStale || needsRefresh ? 'warning' : 'success'}
            size="small"
          />
          <Tooltip title="Refresh rates from backend">
            <span>
              <IconButton
                onClick={refreshRates}
                disabled={loading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === 'string'
            ? error
            : 'An error occurred while loading interest rates'}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Base Rate Display */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Backend Base Rate
            </Typography>
            <Typography variant="h4" color="primary">
              {formatRate(baseRate)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last updated: {backendRates.lastUpdated || 'Never'}
            </Typography>
          </Box>
        </Grid>

        {/* Effective Rate Display */}
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Effective Rate
            </Typography>
            <Typography variant="h4" color="secondary">
              {formatRate(effectiveRate)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              With all adjustments applied
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Adjustments Section */}
      {showAdjustments && (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">Local Adjustments</Typography>
            <Button size="small" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? 'Hide' : 'Show'} Advanced
            </Button>
          </Box>

          {showAdvanced && (
            <Grid container spacing={2}>
              {/* Base Adjustment */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Base Rate Adjustment: {adjustments?.baseAdjustment || 0}%
                </Typography>
                <Slider
                  value={adjustments?.baseAdjustment || 0}
                  onChange={(_, value) => handleBaseAdjustment(value as number)}
                  min={-2}
                  max={2}
                  step={0.125}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Grid>

              {/* Credit Score Adjustment */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Credit Score Adjustment:{' '}
                  {adjustments?.creditScoreAdjustment || 0}%
                </Typography>
                <Slider
                  value={adjustments?.creditScoreAdjustment || 0}
                  onChange={(_, value) =>
                    handleCreditScoreAdjustment(value as number)
                  }
                  min={-1}
                  max={1}
                  step={0.125}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Grid>

              {/* LTV Adjustment */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  LTV Adjustment: {adjustments?.ltvAdjustment || 0}%
                </Typography>
                <Slider
                  value={adjustments?.ltvAdjustment || 0}
                  onChange={(_, value) => handleLTVAdjustment(value as number)}
                  min={-0.5}
                  max={0.5}
                  step={0.125}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                />
              </Grid>

              {/* Custom Rate */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  Custom Rate (overrides all adjustments)
                </Typography>
                <TextField
                  type="number"
                  value={adjustments?.customRate || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    handleCustomRate(isNaN(value) ? 0 : value);
                  }}
                  placeholder="Enter custom rate"
                  size="small"
                  inputProps={{
                    step: 0.125,
                    min: 0,
                    max: 20,
                  }}
                />
              </Grid>
            </Grid>
          )}

          {/* Adjustment Summary */}
          {adjustments &&
            Object.values(adjustments).some((val) => val !== 0) && (
              <Box
                sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Typography variant="body2" gutterBottom>
                  <strong>Current Adjustments:</strong>
                </Typography>
                <Grid container spacing={1}>
                  {adjustments.baseAdjustment !== 0 && (
                    <Grid item xs={6}>
                      <Chip
                        label={`Base: ${
                          adjustments.baseAdjustment > 0 ? '+' : ''
                        }${adjustments.baseAdjustment}%`}
                        color={
                          adjustments.baseAdjustment > 0 ? 'success' : 'error'
                        }
                        size="small"
                      />
                    </Grid>
                  )}
                  {adjustments.creditScoreAdjustment !== 0 && (
                    <Grid item xs={6}>
                      <Chip
                        label={`Credit: ${
                          adjustments.creditScoreAdjustment > 0 ? '+' : ''
                        }${adjustments.creditScoreAdjustment}%`}
                        color={
                          adjustments.creditScoreAdjustment > 0
                            ? 'success'
                            : 'error'
                        }
                        size="small"
                      />
                    </Grid>
                  )}
                  {adjustments.ltvAdjustment !== 0 && (
                    <Grid item xs={6}>
                      <Chip
                        label={`LTV: ${
                          adjustments.ltvAdjustment > 0 ? '+' : ''
                        }${adjustments.ltvAdjustment}%`}
                        color={
                          adjustments.ltvAdjustment > 0 ? 'success' : 'error'
                        }
                        size="small"
                      />
                    </Grid>
                  )}
                  {adjustments.customRate !== undefined && (
                    <Grid item xs={6}>
                      <Chip
                        label={`Custom: ${adjustments.customRate}%`}
                        color="warning"
                        size="small"
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

          {/* Action Buttons */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              disabled={
                !adjustments ||
                Object.values(adjustments).every((val) => val === 0)
              }
            >
              Reset Adjustments
            </Button>
          </Box>
        </Box>
      )}

      {/* Backend Adjustments Info */}
      {(creditScoreAdjustments || ltvAdjustments) && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Backend Adjustments Applied:</strong>
          </Typography>
          {creditScoreAdjustments && (
            <Typography variant="caption" display="block">
              Credit Score: Excellent (-
              {Math.abs(creditScoreAdjustments.excellent)}%), Good (-
              {Math.abs(creditScoreAdjustments.good)}%), Fair (-
              {Math.abs(creditScoreAdjustments.fair)}%), Poor (+
              {creditScoreAdjustments.poor}%)
            </Typography>
          )}
          {ltvAdjustments && (
            <Typography variant="caption" display="block">
              LTV: Low ({ltvAdjustments.low}%), Moderate (+
              {ltvAdjustments.moderate}%), High (+{ltvAdjustments.high}%), Very
              High (+{ltvAdjustments.veryHigh}%)
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
};
